import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  addDoc, 
  collection,
  serverTimestamp 
} from "firebase/firestore";

function TournamentRegistration() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    name: user?.nickname || "",
    phone: user?.phone || "",
    email: user?.email || "",
    experience: "",
    teamName: "",
    emergencyContact: "",
    emergencyPhone: "",
    medicalInfo: "",
    agreeTerms: false,
    agreePrivacy: false,
    survey: {
      motivation: "",
      strengthWeakness: "",
      expectation: ""
    }
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTournamentInfo();
  }, [tournamentId, user, navigate]);

  const fetchTournamentInfo = async () => {
    try {
      const docRef = doc(db, "tournaments", tournamentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const tournamentData = { id: docSnap.id, ...docSnap.data() };
        setTournament(tournamentData);
        
        // 이미 참가한 경우
        if (tournamentData.participants?.some(p => p.userId === user.id)) {
          setError("이미 참가 신청하셨습니다.");
          return;
        }
        
        // 참가 불가능한 상태 체크
        if (tournamentData.status !== 'registration') {
          setError("현재 참가 신청 기간이 아닙니다.");
          return;
        }
        
        const now = new Date();
        const regEnd = tournamentData.registrationEnd?.toDate ? tournamentData.registrationEnd.toDate() : new Date(tournamentData.registrationEnd);
        if (now > regEnd) {
          setError("참가 신청 기간이 마감되었습니다.");
          return;
        }
        
        // 정원 초과 체크
        if (tournamentData.participants?.length >= tournamentData.maxParticipants) {
          setError("참가자 정원이 가득 찼습니다.");
          return;
        }
        
      } else {
        setError("대회를 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("대회 정보 로딩 실패:", error);
      setError("대회 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSurveyChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      survey: {
        ...prev.survey,
        [field]: value
      }
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("참가자명을 입력해주세요.");
      return false;
    }
    if (!formData.phone.trim()) {
      setError("연락처를 입력해주세요.");
      return false;
    }
    if (!formData.email.trim()) {
      setError("이메일을 입력해주세요.");
      return false;
    }
    if (!formData.emergencyContact.trim()) {
      setError("비상연락처 이름을 입력해주세요.");
      return false;
    }
    if (!formData.emergencyPhone.trim()) {
      setError("비상연락처 번호를 입력해주세요.");
      return false;
    }
    if (!formData.agreeTerms) {
      setError("대회 규정에 동의해주세요.");
      return false;
    }
    if (!formData.agreePrivacy) {
      setError("개인정보 처리방침에 동의해주세요.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      // 참가자 정보
      const participantData = {
        userId: user.id,
        name: formData.name,
        nickname: user.nickname,
        phone: formData.phone,
        email: formData.email,
        experience: Array.isArray(formData.experience) ? formData.experience.join(', ') : formData.experience,
        teamName: Array.isArray(formData.teamName) ? formData.teamName.join(', ') : formData.teamName,
        emergencyContact: formData.emergencyContact,
        emergencyPhone: formData.emergencyPhone,
        medicalInfo: Array.isArray(formData.medicalInfo) ? formData.medicalInfo.join(', ') : formData.medicalInfo,
        registeredAt: Date.now(),
        survey: formData.survey // 설문 응답 추가
      };

      // 참가 신청만 등록, 참가자 배열에는 추가하지 않음
      await addDoc(collection(db, "tournamentRegistrations"), {
        tournamentId,
        tournamentName: tournament.name,
        userId: user.id,
        participantData,
        status: "pending",
        registeredAt: serverTimestamp()
      });

      // 주최자에게 알림 전송
      await addDoc(collection(db, "notifications"), {
        from: user.id,
        to: tournament.organizerId,
        type: "tournament_registration",
        tournamentId,
        tournamentName: tournament.name,
        participantName: formData.name,
        status: "pending",
        createdAt: serverTimestamp(),
        message: `${formData.name}님이 ${tournament.name} 대회에 참가 신청했습니다.`
      });

      alert("참가 신청이 완료되었습니다! 승인 후 참가가 확정됩니다.");
      navigate(`/tournament/${tournamentId}`);
      
    } catch (error) {
      console.error("참가 신청 실패:", error);
      setError("참가 신청 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 16,
    transition: "all 0.3s ease",
    background: "#fff"
  };

  const focusStyle = {
    borderColor: "#667eea",
    boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)"
  };

  // 협회 계정은 대회 참가 불가
  if (user?.userType === 'association') {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6366f1', fontWeight: 700, fontSize: 22 }}>
        협회 계정은 대회에 참가할 수 없습니다.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: 18,
        color: '#6b7280'
      }}>
        📝 참가 신청 페이지 로딩중...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: 600,
        margin: '40px auto',
        padding: 40,
        textAlign: 'center',
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>❌</div>
        <h3 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: '#ef4444' }}>
          참가 신청 불가
        </h3>
        <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 24 }}>
          {error}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={() => navigate(`/tournament/${tournamentId}`)}
            style={{
              background: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            대회 정보 보기
          </button>
          <button
            onClick={() => navigate('/tournaments')}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            다른 대회 찾기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 700,
      margin: '40px auto',
      padding: '0 20px'
    }}>
      {/* 헤더 */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 24,
        padding: '32px',
        color: '#fff',
        marginBottom: 32,
        textAlign: 'center',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
      }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 900,
          margin: 0,
          marginBottom: 8,
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          📝 참가 신청
        </h1>
        <h2 style={{
          fontSize: 20,
          fontWeight: 600,
          margin: 0,
          opacity: 0.9
        }}>
          {tournament?.name}
        </h2>
      </div>

      {/* 대회 요약 정보 */}
      <div style={{
        background: '#fff',
        borderRadius: 20,
        padding: 24,
        marginBottom: 32,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f1f5f9'
      }}>
        <h3 style={{
          fontSize: 18,
          fontWeight: 700,
          marginBottom: 16,
          color: '#1f2937'
        }}>
          🏆 대회 정보
        </h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>종목</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
              {tournament?.sport}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>대회 기간</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
              {formatDate(tournament?.startDate)} ~ {formatDate(tournament?.endDate)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>참가비</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
              {tournament?.entryFee > 0 ? `${tournament.entryFee.toLocaleString()}원` : '무료'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>현재 참가자</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
              {tournament?.participants?.length || 0}/{tournament?.maxParticipants}명
            </div>
          </div>
        </div>
      </div>

      {/* 신청 폼 */}
      <form onSubmit={handleSubmit}>
        {/* 개인정보 입력 섹션 타이틀 명확히 */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 24,
            color: '#1f2937',
            borderBottom: '2px solid #f3f4f6',
            paddingBottom: 12
          }}>
            👤 개인정보 입력
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                참가자명 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, focusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                연락처 *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="010-1234-5678"
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, focusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
              이메일 *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="example@email.com"
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, focusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                경력/실력 수준
              </label>
              <select
                value={formData.experience}
                onChange={(e) => handleInputChange("experience", e.target.value)}
                style={inputStyle}
              >
                <option value="">선택하세요</option>
                <option value="초급">초급 (1년 미만)</option>
                <option value="중급">중급 (1-3년)</option>
                <option value="고급">고급 (3-5년)</option>
                <option value="전문가">전문가 (5년 이상)</option>
                <option value="프로">프로/선수</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                팀명 (해당시)
              </label>
              <input
                type="text"
                value={formData.teamName}
                onChange={(e) => handleInputChange("teamName", e.target.value)}
                placeholder="개인 참가시 공란"
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, focusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>
        </div>

        {/* 비상 연락처 */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 24,
            color: '#1f2937',
            borderBottom: '2px solid #f3f4f6',
            paddingBottom: 12
          }}>
            🚨 비상 연락처
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                비상연락처 이름 *
              </label>
              <input
                type="text"
                value={formData.emergencyContact}
                onChange={(e) => handleInputChange("emergencyContact", e.target.value)}
                placeholder="예: 홍길동 (부모님)"
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, focusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                비상연락처 번호 *
              </label>
              <input
                type="tel"
                value={formData.emergencyPhone}
                onChange={(e) => handleInputChange("emergencyPhone", e.target.value)}
                placeholder="010-1234-5678"
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, focusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
              의료 정보 / 특이사항
            </label>
            <textarea
              value={formData.medicalInfo}
              onChange={(e) => handleInputChange("medicalInfo", e.target.value)}
              placeholder="알레르기, 복용 중인 약물, 기타 주의사항 등 (선택사항)"
              rows={3}
              style={{
                ...inputStyle,
                resize: "vertical"
              }}
              onFocus={(e) => Object.assign(e.target.style, focusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>

        {/* 설문(추가 질문) 섹션 추가 */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 24,
            color: '#1f2937',
            borderBottom: '2px solid #f3f4f6',
            paddingBottom: 12
          }}>
            📝 설문 (추가 질문)
          </h3>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
              참가 동기 (자유롭게 작성)
            </label>
            <textarea
              value={formData.survey.motivation}
              onChange={e => handleSurveyChange('motivation', e.target.value)}
              placeholder="이 대회에 참가하게 된 계기, 목표 등"
              rows={2}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 16, resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
              본인의 강점/약점 (자유롭게 작성)
            </label>
            <textarea
              value={formData.survey.strengthWeakness}
              onChange={e => handleSurveyChange('strengthWeakness', e.target.value)}
              placeholder="스포츠 실력, 성격, 팀워크 등"
              rows={2}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 16, resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
              대회에 바라는 점 (자유롭게 작성)
            </label>
            <textarea
              value={formData.survey.expectation}
              onChange={e => handleSurveyChange('expectation', e.target.value)}
              placeholder="운영, 분위기, 기대하는 점 등"
              rows={2}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 16, resize: 'vertical' }}
            />
          </div>
        </div>

        {/* 약관 동의 */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: 20,
            fontWeight: 700,
            marginBottom: 24,
            color: '#1f2937',
            borderBottom: '2px solid #f3f4f6',
            paddingBottom: 12
          }}>
            📋 약관 동의
          </h3>

          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
            fontSize: 14,
            lineHeight: 1.6,
            color: '#374151',
            maxHeight: 200,
            overflowY: 'auto'
          }}>
            <h4 style={{ fontWeight: 600, marginBottom: 12 }}>대회 참가 규정</h4>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>참가자는 대회 규칙을 준수해야 합니다.</li>
              <li>대회 중 발생하는 부상에 대해서는 참가자 본인이 책임을 집니다.</li>
              <li>대회 일정은 주최측 사정에 의해 변경될 수 있습니다.</li>
              <li>참가비는 환불되지 않습니다. (단, 주최측 귀책사유 제외)</li>
              <li>대회 진행을 위한 사진/영상 촬영에 동의합니다.</li>
              <li>허위 정보 제공시 참가 자격이 박탈될 수 있습니다.</li>
              <li>대회 중 부정행위 적발시 즉시 실격 처리됩니다.</li>
            </ul>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 600
            }}>
              <input
                type="checkbox"
                checked={formData.agreeTerms}
                onChange={(e) => handleInputChange("agreeTerms", e.target.checked)}
                style={{ width: 20, height: 20 }}
              />
              <span style={{ color: formData.agreeTerms ? '#10b981' : '#6b7280' }}>
                대회 참가 규정에 동의합니다 *
              </span>
            </label>
          </div>

          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 600
            }}>
              <input
                type="checkbox"
                checked={formData.agreePrivacy}
                onChange={(e) => handleInputChange("agreePrivacy", e.target.checked)}
                style={{ width: 20, height: 20 }}
              />
              <span style={{ color: formData.agreePrivacy ? '#10b981' : '#6b7280' }}>
                개인정보 수집 및 이용에 동의합니다 *
              </span>
            </label>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            color: '#dc2626',
            fontSize: 14,
            fontWeight: 600,
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* 참가비 안내 */}
        {tournament?.entryFee > 0 && (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 12,
            padding: 20,
            marginBottom: 24
          }}>
            <h4 style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#92400e',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              💰 참가비 안내
            </h4>
            <div style={{ fontSize: 14, color: '#78350f', lineHeight: 1.6 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>참가비: {tournament.entryFee.toLocaleString()}원</strong>
              </div>
              <div>
                • 참가 신청 완료 후 별도 안내를 통해 입금해주세요<br/>
                • 입금 확인 후 참가가 최종 확정됩니다<br/>
                • 입금 기한: 신청 후 3일 이내
              </div>
            </div>
          </div>
        )}

        {/* 제출 버튼 */}
        <div style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'center',
          marginBottom: 40
        }}>
          <button
            type="button"
            onClick={() => navigate(`/tournament/${tournamentId}`)}
            style={{
              background: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: 12,
              padding: '16px 32px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={e => e.target.style.background = '#e5e7eb'}
            onMouseOut={e => e.target.style.background = '#f3f4f6'}
          >
            취소
          </button>
          
          <button
            type="submit"
            disabled={submitting || !formData.agreeTerms || !formData.agreePrivacy}
            style={{
              background: (submitting || !formData.agreeTerms || !formData.agreePrivacy) 
                ? '#9ca3af' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '16px 32px',
              fontSize: 16,
              fontWeight: 700,
              cursor: (submitting || !formData.agreeTerms || !formData.agreePrivacy) 
                ? 'not-allowed' 
                : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: (submitting || !formData.agreeTerms || !formData.agreePrivacy) 
                ? 'none' 
                : '0 4px 15px rgba(102, 126, 234, 0.4)',
              minWidth: 140
            }}
            onMouseOver={e => {
              if (!submitting && formData.agreeTerms && formData.agreePrivacy) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
              }
            }}
            onMouseOut={e => {
              if (!submitting && formData.agreeTerms && formData.agreePrivacy) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }
            }}
          >
            {submitting ? '신청 중...' : '📝 참가 신청하기'}
          </button>
        </div>
      </form>

      {/* 안내 메시지 */}
      <div style={{
        background: '#f0f9ff',
        border: '1px solid #3b82f6',
        borderRadius: 12,
        padding: 20,
        textAlign: 'center',
        fontSize: 14,
        color: '#1e40af'
      }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          🔔 참가 신청 안내
        </div>
        <div>
          신청 완료 후 주최자가 확인하여 최종 승인됩니다.<br/>
          승인 결과는 알림으로 안내해드립니다.
        </div>
      </div>
    </div>
  );
}

export default TournamentRegistration;